import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Users, Mail, RefreshCw, Globe, Send } from "lucide-react";
import { api } from "@/lib/api";

const LEAD_COLS = ["created_at", "name", "company", "partner_type", "email", "status", "message"];
const SUB_COLS = ["created_at", "email"];
const LEAD_STATUSES = ["new", "contacted", "won"];
const STATUS_STYLE = {
  new: "border-gold/40 text-gold",
  contacted: "border-terra/60 text-terra",
  won: "border-green-500/50 text-green-500",
};
const STATUS_ACTIVE_BG = { new: "bg-gold/15", contacted: "bg-terra/15", won: "bg-green-500/15" };
const TABS = ["leads", "subscribers", "subdomains", "blast", "settings"];

function toCsv(rows, cols) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

function downloadCsv(filename, rows, cols) {
  const blob = new Blob([toCsv(rows, cols)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [subdomains, setSubdomains] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [blastSubject, setBlastSubject] = useState("");
  const [blastMessage, setBlastMessage] = useState("");
  const [blasts, setBlasts] = useState([]);
  const [blastSending, setBlastSending] = useState(false);
  const [blastConfirming, setBlastConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, d, st, b] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/subscribers"),
        api.get("/subdomains"),
        api.get("/admin/settings"),
        api.get("/admin/blasts"),
      ]);
      setLeads(l.data);
      setSubs(s.data);
      setSubdomains(d.data);
      setNotifyEmail(st.data.notify_email);
      setBlasts(b.data);
    } catch {
      toast.error("Failed to load data. Please sign in again.");
      localStorage.removeItem("tc_admin_token");
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem("tc_admin_token");
    onLogout();
  };

  const setLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const saveSubdomain = async (s) => {
    try {
      await api.put(`/admin/subdomains/${s.key}`, { status: s.status, href: s.href || "" });
      toast.success(`${s.domain} updated — the landing page reflects it immediately.`);
    } catch {
      toast.error("Could not update subdomain.");
    }
  };

  const sendBlast = async () => {
    setBlastConfirming(false);
    setBlastSending(true);
    try {
      const { data } = await api.post("/admin/newsletter-blast", {
        subject: blastSubject,
        message: blastMessage,
      });
      toast.success(`Blast is sending to ${data.total} subscriber${data.total === 1 ? "" : "s"} in the background.`);
      setBlastSubject("");
      setBlastMessage("");
      const b = await api.get("/admin/blasts");
      setBlasts(b.data);
      setTimeout(async () => {
        try {
          const r = await api.get("/admin/blasts");
          setBlasts(r.data);
        } catch { /* ignore */ }
      }, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the blast.");
    } finally {
      setBlastSending(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { notify_email: notifyEmail });
      toast.success(`Lead alerts now go to ${notifyEmail}`);
    } catch {
      toast.error("Could not save settings.");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink px-4 py-10 sm:px-8 lg:px-16">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="kicker">Admin</p>
            <h1 className="mt-2 font-serif text-4xl font-semibold text-cream">
              Leads <span className="text-gold">Dashboard</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button onClick={load} className="btn-ghost !px-5 !py-2.5 !text-xs">
              <RefreshCw size={13} /> Refresh
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="btn-ghost !px-5 !py-2.5 !text-xs">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <div className="rounded-2xl border border-goldline bg-panel/70 p-7">
            <div className="flex items-center gap-3">
              <Users size={18} className="text-gold" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">Partner applications</p>
            </div>
            <p data-testid="stat-leads-count" className="mt-3 font-serif text-5xl text-cream">{leads.length}</p>
          </div>
          <div className="rounded-2xl border border-goldline bg-panel/70 p-7">
            <div className="flex items-center gap-3">
              <Mail size={18} className="text-gold" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">Newsletter subscribers</p>
            </div>
            <p data-testid="stat-subscribers-count" className="mt-3 font-serif text-5xl text-cream">{subs.length}</p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`rounded-full border px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  tab === t ? "border-gold bg-gold/10 text-gold" : "border-line text-muted hover:text-cream"
                }`}
              >
                {t}
                {t === "leads" && ` (${leads.length})`}
                {t === "subscribers" && ` (${subs.length})`}
              </button>
            ))}
          </div>
          {tab === "leads" && (
            <button
              data-testid="export-leads-button"
              onClick={() => downloadCsv("tourism-connect-leads.csv", leads, LEAD_COLS)}
              className="btn-gold !px-5 !py-2.5 !text-xs"
            >
              <Download size={13} /> Export leads CSV
            </button>
          )}
          {tab === "subscribers" && (
            <button
              data-testid="export-subscribers-button"
              onClick={() => downloadCsv("tourism-connect-subscribers.csv", subs, SUB_COLS)}
              className="btn-gold !px-5 !py-2.5 !text-xs"
            >
              <Download size={13} /> Export subscribers CSV
            </button>
          )}
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-line bg-panel/50">
          {loading ? (
            <p className="p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>
          ) : tab === "leads" ? (
            <table data-testid="leads-table" className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Company</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-subtle">No partner applications yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-line transition-colors hover:bg-field">
                    <td className="whitespace-nowrap px-5 py-4 text-muted">{fmt(l.created_at)}</td>
                    <td className="px-5 py-4 text-cream">{l.name}</td>
                    <td className="px-5 py-4 text-cream">{l.company}</td>
                    <td className="px-5 py-4"><span className="rounded-full border border-gold/25 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold/80">{l.partner_type}</span></td>
                    <td className="px-5 py-4 text-muted">{l.email}</td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1.5">
                        {LEAD_STATUSES.map((st) => {
                          const active = (l.status || "new") === st;
                          return (
                            <button
                              key={st}
                              data-testid={`lead-status-${st}-${l.id}`}
                              onClick={() => setLeadStatus(l.id, st)}
                              className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest transition-[opacity,background-color] duration-300 ${STATUS_STYLE[st]} ${
                                active ? STATUS_ACTIVE_BG[st] : "opacity-40 hover:opacity-90"
                              }`}
                            >
                              {st}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="max-w-[220px] truncate px-5 py-4 text-muted">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "subscribers" ? (
            <table data-testid="subscribers-table" className="w-full min-w-[480px] text-left text-sm">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Email</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && (
                  <tr><td colSpan={2} className="px-5 py-10 text-center text-subtle">No subscribers yet.</td></tr>
                )}
                {subs.map((s) => (
                  <tr key={s.id} className="border-b border-line transition-colors hover:bg-field">
                    <td className="whitespace-nowrap px-5 py-4 text-muted">{fmt(s.created_at)}</td>
                    <td className="px-5 py-4 text-cream">{s.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "subdomains" ? (
            <div data-testid="subdomains-panel" className="space-y-4 p-5">
              <p className="px-1 text-sm text-muted">
                Flip a subdomain live the moment it launches — set its URL, toggle the status, save. The landing page updates instantly.
              </p>
              {subdomains.map((s) => (
                <div
                  key={s.key}
                  data-testid={`admin-subdomain-${s.key}`}
                  className="flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-panel/70 p-5"
                >
                  <Globe size={16} className="shrink-0 text-gold" />
                  <div className="min-w-[220px] flex-1">
                    <p className="font-mono text-sm text-cream">{s.domain}</p>
                    <p className="mt-0.5 text-xs text-muted">{s.desc}</p>
                  </div>
                  <input
                    data-testid={`admin-subdomain-url-${s.key}`}
                    placeholder="https://…"
                    value={s.href || ""}
                    onChange={(e) =>
                      setSubdomains((ss) => ss.map((x) => (x.key === s.key ? { ...x, href: e.target.value } : x)))
                    }
                    className="field !w-72"
                  />
                  <button
                    data-testid={`admin-subdomain-toggle-${s.key}`}
                    onClick={() =>
                      setSubdomains((ss) =>
                        ss.map((x) => (x.key === s.key ? { ...x, status: x.status === "live" ? "soon" : "live" } : x))
                      )
                    }
                    className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      s.status === "live"
                        ? "border-green-500/40 bg-green-500/10 text-green-500"
                        : "border-gold/30 text-gold/70"
                    }`}
                  >
                    {s.status === "live" ? "● Live" : "Coming soon"}
                  </button>
                  <button
                    data-testid={`admin-subdomain-save-${s.key}`}
                    onClick={() => saveSubdomain(s)}
                    className="btn-gold !px-5 !py-2 !text-xs"
                  >
                    Save
                  </button>
                </div>
              ))}
            </div>
          ) : tab === "blast" ? (
            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <div
                data-testid="blast-compose-form"
                className="rounded-2xl border border-line bg-panel/70 p-8"
              >
                <p className="kicker mb-3">Newsletter blast</p>
                <p className="text-sm leading-relaxed text-muted">
                  Sends immediately to all {subs.length} opted-in subscriber{subs.length === 1 ? "" : "s"}.
                </p>
                <input
                  data-testid="blast-subject-input"
                  required
                  maxLength={200}
                  value={blastSubject}
                  onChange={(e) => setBlastSubject(e.target.value)}
                  placeholder="Subject line"
                  className="field mt-5"
                />
                <textarea
                  data-testid="blast-message-input"
                  required
                  rows={7}
                  maxLength={8000}
                  value={blastMessage}
                  onChange={(e) => setBlastMessage(e.target.value)}
                  placeholder="Write your update — new subdomains, partner news, seasonal offers…"
                  className="field mt-4"
                />
                {blastConfirming ? (
                  <div className="mt-4 rounded-xl border border-terra/40 bg-terra/10 p-4">
                    <p data-testid="blast-confirm-text" className="text-xs leading-relaxed text-terra">
                      Send this blast to all {subs.length} subscriber{subs.length === 1 ? "" : "s"} now? This can't be undone.
                    </p>
                    <div className="mt-3 flex gap-3">
                      <button
                        data-testid="blast-confirm-button"
                        type="button"
                        onClick={sendBlast}
                        disabled={blastSending}
                        className="btn-gold !px-5 !py-2 !text-xs disabled:opacity-50"
                      >
                        {blastSending ? "Queuing…" : "Confirm send"}
                      </button>
                      <button
                        data-testid="blast-cancel-button"
                        type="button"
                        onClick={() => setBlastConfirming(false)}
                        className="btn-ghost !px-5 !py-2 !text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid="blast-send-button"
                    type="button"
                    onClick={() => {
                      if (!blastSubject.trim() || !blastMessage.trim()) {
                        toast.error("Add a subject and message first.");
                        return;
                      }
                      setBlastConfirming(true);
                    }}
                    disabled={subs.length === 0}
                    className="btn-gold mt-4 !px-5 !py-2.5 !text-xs disabled:opacity-50"
                  >
                    <Send size={13} /> Send blast
                  </button>
                )}
              </div>
              <div data-testid="blast-history" className="rounded-2xl border border-line bg-panel/70 p-8">
                <p className="kicker mb-4">Send history</p>
                {blasts.length === 0 && <p className="text-sm text-muted">No blasts sent yet.</p>}
                <div className="max-h-80 space-y-1 overflow-y-auto">
                  {blasts.map((b) => (
                    <div key={b.id} data-testid={`blast-item-${b.id}`} className="border-b border-line py-3 last:border-0">
                      <p className="text-sm text-cream">{b.subject}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                        {fmt(b.created_at)} ·{" "}
                        {b.state === "sending"
                          ? `sending… ${b.sent}/${b.total ?? "?"}`
                          : `${b.sent} sent${b.failed ? ` · ${b.failed} failed` : ""}`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <form
                onSubmit={saveSettings}
                className="max-w-lg rounded-2xl border border-line bg-panel/70 p-8"
                data-testid="settings-panel"
              >
                <p className="kicker mb-3">Lead alert inbox</p>
                <p className="text-sm leading-relaxed text-muted">
                  Every new partner application is emailed to this address the moment it arrives.
                </p>
                <input
                  data-testid="settings-notify-email-input"
                  type="email"
                  required
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="you@tourism-connect.com"
                  className="field mt-5"
                />
                <button data-testid="settings-save-button" type="submit" className="btn-gold mt-4 !px-5 !py-2.5 !text-xs">
                  Save alert inbox
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
